import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChatbubbleEllipsesOutline, IoArrowForward } from "react-icons/io5";
import { useAuth } from "@/context/AuthContext";
import { adminUnseenReview } from "@/lib/api";

const NERODUS_IMG = "/nerodus.png";

/**
 * Notification centrale (admin) : prévient dès qu'une nouvelle relecture est
 * demandée ou qu'un élève répond, avec qui/quoi et un accès direct.
 * Montée globalement dans DojoShell pour l'administrateur.
 */
export default function ReviewAlertPrompt() {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  const check = async () => {
    try {
      const d = await adminUnseenReview();
      if (d && d.unseen) setItem(d); else setItem(null);
    } catch { /* silencieux */ }
  };

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return undefined;
    check();
    const id = setInterval(check, 20000);
    return () => clearInterval(id);
  }, [isAuthenticated, isAdmin]);

  if (!isAuthenticated || !isAdmin || !item) return null;
  if (dismissed.includes(item.entry_id)) return null;

  const isReply = item.kind === "reply";
  const go = () => { const id = item.entry_id; setItem(null); nav(`/carnet/eleves?open=${id}`); };

  return (
    <div style={s.overlay} data-testid="review-alert-overlay">
      <div style={s.card} role="dialog" aria-modal="true" data-testid="review-alert-card">
        <div style={s.glow} />
        <img src={NERODUS_IMG} alt="Nerodus" style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
        <div style={s.pill}><IoChatbubbleEllipsesOutline size={14} /> {isReply ? "Nouvelle réponse" : "Nouvelle relecture"}</div>
        <h2 style={s.title}>
          {isReply ? <> <strong>{item.member_name}</strong> t'a répondu</> : <> <strong>{item.member_name}</strong> demande une relecture</>}
        </h2>
        <p style={s.body}>
          {isReply ? "Un nouveau message t'attend sur " : "Nouvelle demande sur "}
          <strong>« {item.title} »</strong>.
        </p>
        <div style={s.actions}>
          <button style={s.later} data-testid="review-alert-later" onClick={() => setDismissed((d) => [...d, item.entry_id])}>Plus tard</button>
          <button style={s.go} data-testid="review-alert-go" onClick={go}>Ouvrir la relecture <IoArrowForward size={17} /></button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: "fixed", inset: 0, zIndex: 7000, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18, animation: "raFade .25s ease" },
  card: { position: "relative", overflow: "hidden", width: "100%", maxWidth: 420, textAlign: "center", padding: "34px 28px 24px", borderRadius: 24, border: "1px solid color-mix(in srgb, var(--brand) 40%, var(--border-c))", background: "var(--bg-secondary)", boxShadow: "0 40px 100px rgba(0,0,0,0.5)", animation: "raPop .32s cubic-bezier(.2,.9,.3,1.2) both" },
  glow: { position: "absolute", top: -90, left: "50%", transform: "translateX(-50%)", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 30%, transparent), transparent 70%)" },
  avatar: { position: "relative", zIndex: 1, width: 84, height: 84, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "3px solid var(--brand)", boxShadow: "0 10px 30px rgba(0,0,0,0.35)" },
  pill: { position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14, padding: "5px 13px", borderRadius: 999, background: "color-mix(in srgb, var(--brand) 16%, transparent)", color: "var(--brand)", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.5 },
  title: { position: "relative", zIndex: 1, margin: "12px 0 6px", fontSize: 21, fontWeight: 900, color: "var(--text-primary)" },
  body: { position: "relative", zIndex: 1, fontSize: 14.5, lineHeight: 1.55, color: "var(--text-secondary)", margin: "0 auto 22px", maxWidth: 340 },
  actions: { position: "relative", zIndex: 1, display: "flex", gap: 10, justifyContent: "center", alignItems: "center" },
  go: { display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 22px", borderRadius: 999, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer" },
  later: { padding: "13px 18px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "transparent", color: "var(--text-secondary)", fontWeight: 800, fontSize: 14, cursor: "pointer" },
};

if (typeof document !== "undefined" && !document.getElementById("ra-prompt-kf")) {
  const el = document.createElement("style");
  el.id = "ra-prompt-kf";
  el.textContent = "@keyframes raFade{from{opacity:0}to{opacity:1}}@keyframes raPop{from{opacity:0;transform:translateY(16px) scale(.96)}to{opacity:1;transform:none}}";
  document.head.appendChild(el);
}
